import React, { useCallback, useMemo, useState } from 'react'
import InfiniteScroll from 'react-infinite-scroller'
import { Drawer, Empty, Input, Skeleton, Spin, theme } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import { useIntl } from 'react-intl'

import { encodeUriWithBtoa } from 'utils/helper'
import { qtyToCurrencyAutoComma } from 'utils/formatting'
import useNavigation from 'utils/hooks/useNavigation'
import useDebounceValue from 'utils/hooks/useDebounceValue'
import { useInfiniteLowStockQuery } from 'utils/queries/lowStock'
import { buildLowStockCssVars } from './cssVars'

const ChevronIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    width="14"
    height="14"
  >
    <path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
)

const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

// Tandai bagian teks yang cocok dengan kata kunci pencarian.
const Highlight = ({ text, keyword }) => {
  const parts = useMemo(() => {
    const value = String(text ?? '')
    if (!keyword) {
      return [{ key: 0, part: value, isMatch: false }]
    }
    let offset = 0
    return value
      .split(new RegExp(`(${escapeRegExp(keyword)})`, 'i'))
      .map((part, idx) => {
        const item = { key: offset, part, isMatch: idx % 2 === 1 }
        offset += part.length
        return item
      })
      .filter((item) => item.part)
  }, [text, keyword])

  return parts.map((item) =>
    item.isMatch ? (
      <mark key={item.key} className="low-stock-drawer__hl">
        {item.part}
      </mark>
    ) : (
      <React.Fragment key={item.key}>{item.part}</React.Fragment>
    ),
  )
}

const SKELETON_ROWS = [0, 1, 2, 3]

const ListSkeleton = () =>
  SKELETON_ROWS.map((row) => (
    <div key={row} className="low-stock-drawer__row is-skeleton">
      <Skeleton active title={{ width: '55%' }} paragraph={{ rows: 2, width: ['30%', '75%'] }} />
    </div>
  ))

const ProductRow = ({ product, tone, keyword, formatMessage, onView }) => {
  const percent = useMemo(() => {
    const buffer = Number(product?.buffer_qty) || 0
    const qty = Number(product?.qty) || 0
    if (buffer <= 0) {
      return qty > 0 ? 100 : 0
    }
    return Math.max(0, Math.min(100, (qty / buffer) * 100))
  }, [product])

  const handleView = useCallback(() => {
    onView(product?.id)
  }, [onView, product?.id])

  return (
    <div className="low-stock-drawer__row">
      <div className="low-stock-drawer__row-main">
        <div className="low-stock-drawer__name">
          <Highlight text={product?.name} keyword={keyword} />
        </div>
        <div className="low-stock-drawer__sku">
          <Highlight text={product?.code} keyword={keyword} />
        </div>
      </div>
      {onView && (
        <button type="button" className="low-stock-drawer__view" onClick={handleView}>
          {formatMessage({ id: 'lowStock.view' })}
          <ChevronIcon />
        </button>
      )}
      <div className="low-stock-drawer__stock">
        <div className="low-stock-drawer__stock-nums">
          {formatMessage({ id: 'financeProduct.qty' })}{' '}
          <b>
            {qtyToCurrencyAutoComma({ value: product?.qty ?? 0, maxPrecision: 4 })} {product?.unit}
          </b>{' '}
          · {formatMessage({ id: 'lowStock.min_stock' })}{' '}
          <b>
            {qtyToCurrencyAutoComma({ value: product?.buffer_qty ?? 0, maxPrecision: 4 })}{' '}
            {product?.unit}
          </b>
        </div>
        {tone === 'crit' ? (
          <span className="low-stock-drawer__habis-pill">
            {formatMessage({ id: 'lowStock.chip_empty' })}
          </span>
        ) : (
          <div className="low-stock-drawer__bar">
            <i className={`is-${tone}`} style={{ width: `${percent}%` }} />
          </div>
        )}
      </div>
    </div>
  )
}

// `linkMode="new-tab"` (POS): link produk dibuka di tab baru supaya layar kasir tetap terbuka,
// dan tombol "Lihat semua di menu Produk" disembunyikan karena daftar Produk tidak per outlet.
const DetailDrawer = ({
  open,
  onClose,
  data,
  mobileView,
  outletId,
  pos = false,
  linkMode = 'same-tab',
  canViewProduct = true,
}) => {
  const { formatMessage } = useIntl()
  const { token } = theme.useToken()
  const { onNavigate } = useNavigation()

  const [activeTab, setActiveTab] = useState('almost_empty')
  const [search, setSearch] = useState('')

  const debouncedSearch = useDebounceValue(search, 400)
  const keyword = debouncedSearch.trim()

  const cssVars = useMemo(() => buildLowStockCssVars(token), [token])

  const total = (data?.almost_empty?.count || 0) + (data?.empty?.count || 0)

  // Halaman pertama tanpa pencarian sama dengan data banner, jadi drawer tampil tanpa request baru.
  const initialData = useMemo(
    () => (data && !keyword ? { pages: [data], pageParams: [1] } : undefined),
    [data, keyword],
  )

  const {
    data: infiniteData,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteLowStockQuery({ search: keyword, outletId, pos, initialData, enabled: open })

  const isNewTab = linkMode === 'new-tab'

  const pages = useMemo(() => infiniteData?.pages || [], [infiniteData])
  const firstPage = pages[0]

  const products = useMemo(
    () => pages.flatMap((page) => page?.[activeTab]?.data || []),
    [pages, activeTab],
  )

  const activeLastPage = pages[pages.length - 1]?.[activeTab]
  const activeHasMore =
    !!hasNextPage && !!activeLastPage && activeLastPage.current_page < activeLastPage.last_page
  const activeTotal = firstPage?.[activeTab]?.total || 0

  const handleTabAlmost = useCallback(() => setActiveTab('almost_empty'), [])
  const handleTabEmpty = useCallback(() => setActiveTab('empty'), [])

  const handleSearchChange = useCallback((e) => setSearch(e.target.value), [])

  const handleLoadMore = useCallback(() => {
    fetchNextPage()
  }, [fetchNextPage])

  const handleViewProduct = useCallback(
    (id) => {
      if (!id) {
        return
      }
      if (isNewTab) {
        // Router memakai hash history, jadi URL tab baru harus lewat `#/`.
        window.open(`/#/products/detail/${id}`, '_blank', 'noopener')
        return
      }
      onNavigate(`/products/detail/${id}`)
      onClose()
    },
    [isNewTab, onNavigate, onClose],
  )

  const handleSeeAll = useCallback(() => {
    const filters = encodeUriWithBtoa({
      stock_status: activeTab,
      ...(keyword && { search: keyword }),
    })
    onNavigate(`/products?filters=${filters}`)
    onClose()
  }, [activeTab, keyword, onNavigate, onClose])

  return (
    <Drawer
      open={open}
      onClose={onClose}
      placement="right"
      width={mobileView ? '100%' : 460}
      closable={false}
      styles={{ body: { padding: 0 } }}
      className="low-stock-drawer"
      destroyOnHidden
    >
      <div className="low-stock-drawer__inner" style={cssVars}>
        <div className="low-stock-drawer__head">
          <span className="low-stock-drawer__head-icon">
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--ls-crit-text)"
              strokeWidth="2"
              width="20"
              height="20"
            >
              <path d="M12 3 2 20h20L12 3Z" strokeLinejoin="round" />
              <path d="M12 10v4" strokeLinecap="round" />
              <circle cx="12" cy="17" r="1.1" fill="var(--ls-crit-text)" stroke="none" />
            </svg>
          </span>
          <div className="low-stock-drawer__head-text">
            <h3>{formatMessage({ id: 'lowStock.drawer_title' })}</h3>
            <p>{formatMessage({ id: 'lowStock.drawer_subtitle' }, { total })}</p>
          </div>
          <button
            type="button"
            className="low-stock-drawer__close"
            aria-label="Close"
            onClick={onClose}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.2"
              width="16"
              height="16"
            >
              <path d="M6 6l12 12M18 6 6 18" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="low-stock-drawer__search">
          <Input
            allowClear
            value={search}
            onChange={handleSearchChange}
            prefix={<SearchOutlined />}
            placeholder={formatMessage({ id: 'lowStock.search_placeholder' })}
          />
        </div>

        <div className="low-stock-drawer__seg">
          <button
            type="button"
            className={activeTab === 'almost_empty' ? 'is-active-warn' : ''}
            onClick={handleTabAlmost}
          >
            <span className="cnt warn">{firstPage ? firstPage.almost_empty?.total || 0 : '–'}</span>
            {formatMessage({ id: 'financeProduct.almost_empty' })}
          </button>
          <button
            type="button"
            className={activeTab === 'empty' ? 'is-active-crit' : ''}
            onClick={handleTabEmpty}
          >
            <span className="cnt crit">{firstPage ? firstPage.empty?.total || 0 : '–'}</span>
            {formatMessage({ id: 'lowStock.chip_empty' })}
          </button>
        </div>

        <div className="low-stock-drawer__list" key={`${activeTab}-${keyword}`}>
          {isLoading && <ListSkeleton />}
          {!isLoading && products.length === 0 && (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                keyword
                  ? formatMessage({ id: 'lowStock.no_search_result' }, { search: keyword })
                  : formatMessage({ id: 'lowStock.no_data' })
              }
              style={{ padding: '32px 0' }}
            />
          )}
          {!isLoading && products.length > 0 && (
            <InfiniteScroll
              initialLoad={false}
              pageStart={1}
              loadMore={handleLoadMore}
              hasMore={activeHasMore && !isFetchingNextPage}
              useWindow={false}
            >
              {products.map((product) => (
                <ProductRow
                  key={product.id}
                  product={product}
                  tone={activeTab === 'almost_empty' ? 'warn' : 'crit'}
                  keyword={keyword}
                  formatMessage={formatMessage}
                  onView={canViewProduct ? handleViewProduct : undefined}
                />
              ))}
              {isFetchingNextPage && (
                <div className="low-stock-drawer__loading-more">
                  <Spin size="small" />
                </div>
              )}
            </InfiniteScroll>
          )}
        </div>

        {(!isNewTab || activeTotal > 0) && (
          <div className="low-stock-drawer__foot">
            {activeTotal > 0 && (
              <div
                className="low-stock-drawer__progress"
                style={isNewTab ? { marginBottom: 0 } : undefined}
              >
                {formatMessage(
                  { id: 'lowStock.showing_count' },
                  { shown: products.length, total: activeTotal },
                )}
              </div>
            )}
            {!isNewTab && (
              <button type="button" className="low-stock-drawer__foot-btn" onClick={handleSeeAll}>
                {formatMessage({ id: 'lowStock.see_all_products' })}
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  width="15"
                  height="15"
                >
                  <path d="M5 12h14M13 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            )}
          </div>
        )}
      </div>
    </Drawer>
  )
}

export default DetailDrawer
